import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";

import PageTitle from "../../../layouts/PageTitle";
import { selectUser } from "../../../../store/authSlice";
import {
  appApi,
  useGetNcsFollowupsQuery,
  useGetNcsFollowupSummaryQuery,
  useGetNcsStillInactiveSinceYesterdayQuery,
  useGetNcsActivatedQuery,
  useGetNcsDeactivationForecastQuery,
  useGetNcsOverdueBucketsQuery,
  useGetNcsTeamQuery,
} from "../../../../store/appApi";
import "./style.css";
import {
  usePartnerCode,
  useProjectPartners,
  panelClass,
  fieldClass,
  btnGhost,
  Badge,
  FOLLOWUP_STATES,
  firstName,
  fmtDate,
  fmtDateInput,
} from "./config";
import {
  logFollowupCallApi,
  markFollowupPaidApi,
  addCustomerNoteApi,
  downloadWorkbook,
  transferToModemRecoveryApi,
  bulkTransferToModemRecoveryApi,
} from "../../../../services/netplusCustomerStatus";
import { uiPrompt, uiToast, uiAlert } from "./ui";
import CustomerDrawer from "./CustomerDrawer";

const errMsg = (e) => e?.response?.data?.message || "Something went wrong.";

const CALL_OUTCOMES = [
  "no-answer",
  "will-recharge",
  "refused",
  "wrong-number",
  "other",
];

// Filters/inputs on this sheet must stay white-on-dark-text even when the OS or
// browser is in dark mode — hence the explicit inline style alongside the class.
const WHITE_FIELD =
  "rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-800 outline-none";
const WHITE_FIELD_STYLE = { backgroundColor: "#fff", color: "#1f2937" };
const WHITE_OPTION_STYLE = { backgroundColor: "#fff", color: "#1f2937" };

// "Rajesh Kumar" -> "RK". Falls back to a dash when no name is populated.
const initialsOf = (name) =>
  (name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase() || "—";

// The chip shown next to a call/remark: "You" when the current user wrote it,
// otherwise the author's initials.
const authorChip = (by, meId) =>
  by && by._id && meId && String(by._id) === String(meId)
    ? "You"
    : initialsOf(by?.name);

// callLog entries, newest first.
const callHistoryOf = (f) =>
  Array.isArray(f?.callLog)
    ? [...f.callLog].sort((a, b) => new Date(b.at || 0) - new Date(a.at || 0))
    : [];

// Dark accent colour for a follow-up line, keyed off what happened:
//   recovery / modem pickup -> red, recharge / renewal -> green, plain remark -> blue.
const followupTone = (h) => {
  const o = (h?.outcome || "").toLowerCase();
  const n = (h?.note || "").toLowerCase();
  if (o === "will-recharge" || /rechar|renew|paid|activ/.test(n))
    return "#15803d";
  if (
    o === "refused" ||
    o === "wrong-number" ||
    /recover|modem|pick ?up|reclaim|disconnect|remov/.test(n)
  )
    return "#b91c1c";
  return "#1d4ed8";
};

const NcsFollowups = () => {
  const dispatch = useDispatch();
  const me = useSelector(selectUser);
  const meId = me?.localId;
  const isSuperAdmin = me?.role === "super-admin";
  const [partnerCode, setPartnerCode] = usePartnerCode();
  const { codes: allPartnerCodes } = useProjectPartners();
  // "All partners" is local to this screen only — it never overwrites the
  // shared, cross-page selected partner (usePartnerCode/localStorage), so
  // switching to it here doesn't affect Customers/Upload/Modem Recovery/etc.
  const [viewAll, setViewAll] = useState(false);
  const [state, setState] = useState("");
  const [bucket, setBucket] = useState("");
  const [sort, setSort] = useState("daysDown");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [q, setQ] = useState("");
  const [qLive, setQLive] = useState("");
  const [page, setPage] = useState(1);

  // Changing a filter dropdown starts a fresh view — drop any lingering search
  // term so "Open (all)" etc. really shows everything.
  const changeFilter = (setter) => (value) => {
    setter(value);
    setQ("");
    setQLive("");
    setPage(1);
  };

  // Full call / remark history popup for one follow-up, if open.
  const [historyFollowup, setHistoryFollowup] = useState(null);
  // Full customer detail modal (status, active plan, lapse history, …).
  const [detailFor, setDetailFor] = useState(null);
  // Per-row inline "log a call" draft: { [followupId]: { open, outcome, note } }
  const [callDrafts, setCallDrafts] = useState({});
  const [savingCall, setSavingCall] = useState("");

  const getCallDraft = (id) =>
    callDrafts[id] || { open: false, outcome: CALL_OUTCOMES[0], note: "" };
  const setCallDraft = (id, patch) =>
    setCallDrafts((prev) => ({
      ...prev,
      [id]: { ...getCallDraft(id), ...patch },
    }));
  const toggleCall = (id) => setCallDraft(id, { open: !getCallDraft(id).open });

  // Optional date range — off by default so the board still shows the full
  // live open backlog; only applied once from/to is actually picked. Linked
  // to the bucket on the backend: for Inactive/Suspended it filters by
  // deactivatedOn ("Down since"), otherwise by openedOn.
  const dateFilterField =
    bucket === "Inactive" || bucket === "Suspended" || bucket === "lapsed"
      ? "Down since"
      : "Opened on";

  // The summary tiles / upcoming / overdue panels above the board are
  // inherently per-partner, so they're skipped while "All partners" is on —
  // only the follow-up list itself combines every partner into one sheet.
  const isAllView = viewAll;

  const { data, isFetching } = useGetNcsFollowupsQuery({
    partnerCode: viewAll ? "ALL" : partnerCode,
    state,
    bucket,
    sort,
    from: fromDate,
    to: toDate,
    q: qLive,
    page,
    limit: 50,
  });
  const { data: summary } = useGetNcsFollowupSummaryQuery(
    { partnerCode },
    { skip: isAllView },
  );
  const { data: activated } = useGetNcsActivatedQuery(
    { partnerCode, window: 7 },
    { skip: isAllView },
  );
  const { data: forecast } = useGetNcsDeactivationForecastQuery(
    { partnerCode, days: 7, withList: true },
    { skip: isAllView },
  );
  const { data: overdue } = useGetNcsOverdueBucketsQuery(
    { partnerCode, withList: true },
    { skip: isAllView },
  );
  const [expandedOverdueBucket, setExpandedOverdueBucket] = useState(null);
  // Which "already transferred" status card is open (pending / untraceable /
  // recovered / handed over) — those customers are kept out of the day cards.
  const [expandedModemGroup, setExpandedModemGroup] = useState(null);
  // "Worked yesterday, still down" — surfaced after each sheet upload so a
  // stalled recharge claim or unanswered call doesn't quietly fall through.
  // Unlike the other per-partner tiles above, this one also works with
  // partnerCode="ALL" (the backend runs it cross-partner), so it's not
  // skipped in the "All partners" view.
  const { data: stillInactive } = useGetNcsStillInactiveSinceYesterdayQuery({
    partnerCode: isAllView ? "ALL" : partnerCode,
  });
  const [showStillInactive, setShowStillInactive] = useState(false);
  // Which "Day N" card is expanded — null shows no list, same click-to-reveal
  // pattern as the overdue-days cards below.
  const [expandedUpcomingDay, setExpandedUpcomingDay] = useState(null);
  const { data: teamData } = useGetNcsTeamQuery();
  const staff = (teamData?.members || [])
    .filter((m) => m.active !== false)
    .map((m) => ({
      _id: m.userId?._id || m.userId,
      name: m.userId?.name || m.name,
    }));

  const rows = data?.followups || [];
  const totalPages = data?.totalPages || 1;
  const refresh = () =>
    dispatch(appApi.util.invalidateTags(["NetplusCustomerStatus"]));

  // Synthetic (Active-customer) rows always come back with an empty callLog
  // — the note is saved to NetplusCustomerNote, not the row itself — so
  // without this, the activity chip would silently keep showing "Expiring in
  // Xd" after a Call/Paid save and look like nothing happened. Tracked
  // locally, keyed by customerKey, just for this session's immediate feedback.
  const [localRemarks, setLocalRemarks] = useState({});

  // Active-customer rows (isSynthetic) have no real follow-up behind them —
  // there is nothing yet to log a call against or mark paid/recovered.
  // Call/Paid on those rows are recorded as a plain customer remark instead
  // (same note used on the Customer Drawer), so the interaction is saved
  // without inventing a fake lapse or crediting a recovery that never
  // happened.
  const saveCall = async (f) => {
    const draft = getCallDraft(f._id);
    if (!draft.note.trim() && !draft.outcome) return;
    try {
      setSavingCall(f._id);
      if (f.isSynthetic) {
        const text = `[Call — ${draft.outcome}] ${draft.note.trim()}`.trim();
        await addCustomerNoteApi(
          f.customerKey,
          text,
          f.partnerCode || partnerCode,
        );
        setLocalRemarks((prev) => ({ ...prev, [f.customerKey]: text }));
      } else {
        await logFollowupCallApi(
          f._id,
          {
            outcome: draft.outcome,
            promisedDate: null,
            note: draft.note.trim(),
          },
          partnerCode,
        );
      }
      setCallDrafts((prev) => ({
        ...prev,
        [f._id]: { open: false, outcome: CALL_OUTCOMES[0], note: "" },
      }));
      refresh();
      uiToast({
        type: "success",
        title: f.isSynthetic ? "Remark saved" : "Call logged",
      });
    } catch (e) {
      uiToast({
        type: "error",
        title: "Couldn't log call",
        message: errMsg(e),
      });
    } finally {
      setSavingCall("");
    }
  };

  const markPaid = async (f) => {
    const note = await uiPrompt({
      title: `Record payment — ${f.name || f.userId}`,
      message: f.isSynthetic
        ? "This customer is still Active — saved as a remark on their record, not a recovery credit."
        : "Enter the payment details. This marks the follow-up paid right away and credits it to your account — no sheet confirmation needed.",
      label: "Payment details (amount · ref no · mode)",
      placeholder: "e.g. ₹499 · UPI 4432xxxx · GPay",
      multiline: true,
      required: true,
      confirmText: "Mark paid",
    });
    if (note === null) return;
    try {
      if (f.isSynthetic) {
        const text = `[Early payment] ${note}`;
        await addCustomerNoteApi(
          f.customerKey,
          text,
          f.partnerCode || partnerCode,
        );
        setLocalRemarks((prev) => ({ ...prev, [f.customerKey]: text }));
      } else {
        await markFollowupPaidApi(f._id, note, partnerCode);
      }
      refresh();
      uiToast({ type: "success", title: "Payment recorded" });
    } catch (e) {
      const body = e?.response?.data;
      if (e?.response?.status === 409 && body?.reason === "already_closed") {
        refresh();
        const renews = body.expiryDate ? fmtDate(body.expiryDate) : null;
        await uiAlert({
          type: "warning",
          title: "Already marked paid",
          message: [
            body.markedPaidBy
              ? `Already marked paid by ${body.markedPaidBy}${
                  body.markedPaidAt ? ` on ${fmtDate(body.markedPaidAt)}` : ""
                }.`
              : "This follow-up is already closed.",
            body.customerStatus
              ? `Customer is currently ${body.customerStatus}${
                  body.plan ? ` on the ${body.plan} plan` : ""
                }${renews ? `, renews ${renews}` : ""}.`
              : null,
          ].filter(Boolean),
        });
        return;
      }
      uiToast({
        type: "error",
        title: "Couldn't mark paid",
        message: errMsg(e),
      });
    }
  };

  const transfer = async (f) => {
    const res = await uiPrompt({
      title: `Transfer to Modem Recovery — ${f.name || f.userId}`,
      confirmText: "Transfer",
      fields: [
        {
          name: "assignedTo",
          label: "Assign to",
          type: "select",
          options: [
            { value: "", label: "Unassigned" },
            ...staff.map((s) => ({ value: s._id, label: s.name })),
          ],
        },
        {
          name: "reason",
          label: "Reason (optional)",
          type: "textarea",
          placeholder: "reason",
        },
        // Super-admin only — everyone else transfers dated to right now.
        ...(isSuperAdmin
          ? [
              {
                name: "transferredOn",
                label: "Transferred on (back-date if needed)",
                type: "date",
                initial: fmtDateInput(),
                max: fmtDateInput(),
              },
            ]
          : []),
      ],
    });
    if (res === null) return;
    try {
      await transferToModemRecoveryApi({
        // In "All partners" view f.partnerCode is the row's real tenant —
        // the page-level partnerCode is the "ALL" sentinel, not a real code.
        partnerCode: f.partnerCode || partnerCode,
        customerKey: f.customerKey,
        reason: res.reason || "",
        assignedTo: res.assignedTo || null,
        transferredOn: res.transferredOn || undefined,
      });
      refresh();
      uiToast({ type: "success", title: "Moved to modem recovery" });
    } catch (e) {
      uiToast({ type: "error", title: "Transfer failed", message: errMsg(e) });
    }
  };

  // ── Bulk transfer to modem recovery ───────────────────────────────────────
  const [selected, setSelected] = useState(() => new Set());
  const toggleSel = (key) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  const selectableKeys = rows
    .filter((f) => f.isOpen && !f.movedToModemRecovery)
    .map((f) => f.customerKey);
  const allSelected =
    selectableKeys.length > 0 && selectableKeys.every((k) => selected.has(k));
  const toggleSelectAll = () =>
    setSelected(allSelected ? new Set() : new Set(selectableKeys));

  const bulkTransfer = async () => {
    const keys = [...selected];
    if (!keys.length) return;
    const res = await uiPrompt({
      title: `Transfer ${keys.length} customer(s) to Modem Recovery`,
      confirmText: "Transfer all",
      fields: [
        {
          name: "assignedTo",
          label: "Assign to",
          type: "select",
          options: [
            { value: "", label: "Unassigned" },
            ...staff.map((s) => ({ value: s._id, label: s.name })),
          ],
        },
        {
          name: "reason",
          label: "Reason (optional)",
          type: "textarea",
          placeholder: "reason",
        },
        // Super-admin only — everyone else transfers dated to right now.
        ...(isSuperAdmin
          ? [
              {
                name: "transferredOn",
                label: "Transferred on (back-date if needed)",
                type: "date",
                initial: fmtDateInput(),
                max: fmtDateInput(),
              },
            ]
          : []),
      ],
    });
    if (res === null) return;
    try {
      const r = await bulkTransferToModemRecoveryApi({
        partnerCode,
        customerKeys: keys,
        reason: res.reason || "",
        assignedTo: res.assignedTo || null,
        transferredOn: res.transferredOn || undefined,
      });
      setSelected(new Set());
      refresh();
      const skippedList = r?.data?.skipped || [];
      uiToast({
        type: skippedList.length ? "warning" : "success",
        title: `${r?.data?.created ?? 0} of ${keys.length} moved to modem recovery`,
        message: skippedList.length
          ? `Skipped: ${skippedList
              .slice(0, 5)
              .map((s) => `${s.key} (${s.reason})`)
              .join(
                ", ",
              )}${skippedList.length > 5 ? `, +${skippedList.length - 5} more` : ""}`
          : undefined,
      });
    } catch (e) {
      uiToast({
        type: "error",
        title: "Bulk transfer failed",
        message: errMsg(e),
      });
    }
  };

  return (
    <>
      <PageTitle activeMenu="Follow-ups" motherMenu="Netplus" />

      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Stat label="Open" value={summary?.open} tone="text-cyan-600" />
        <Stat
          label="High priority"
          value={summary?.byPriority?.high}
          tone="text-rose-600"
        />
        <Stat
          label="Unassigned"
          value={
            (summary?.byAssignee || []).find((a) => a.name === "Unassigned")
              ?.count ?? "—"
          }
        />
        <Stat
          label="Recovered (7d)"
          value={activated?.count}
          tone="text-emerald-600"
        />
        <Stat
          label="Followed up yesterday, still down"
          value={stillInactive?.customers?.length}
          tone="text-amber-600"
          onClick={
            stillInactive?.customers?.length
              ? () => setShowStillInactive(true)
              : undefined
          }
        />
      </div>

      {showStillInactive && (
        <Modal
          show
          onHide={() => setShowStillInactive(false)}
          size="lg"
          centered
          scrollable
        >
          <Modal.Header closeButton>
            <Modal.Title className="text-base">
              Followed up yesterday, still inactive
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {(stillInactive?.customers || []).length === 0 ? (
              <div className="py-8 text-center text-xs text-gray-400">
                Nothing here.
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {stillInactive.customers.map((c) => (
                  <button
                    key={`${c.partnerCode}::${c.customerKey}`}
                    type="button"
                    className="flex w-full items-center justify-between gap-3 py-2.5 text-left hover:bg-cyan-50/40"
                    onClick={() => {
                      setShowStillInactive(false);
                      setDetailFor({
                        partnerCode: c.partnerCode || partnerCode,
                        customerKey: c.customerKey,
                      });
                    }}
                  >
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-gray-800">
                        {c.name || c.userId}
                        {isAllView && c.partnerCode && (
                          <span className="ml-1.5 text-[10px] font-semibold uppercase text-gray-400">
                            {c.partnerCode}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-x-1.5 text-xs font-medium text-gray-600">
                        <span className="whitespace-nowrap">m:{c.phone || "—"},</span>
                        <span className="whitespace-nowrap">id:{c.userId || "—"}</span>
                      </div>
                      {c.markedPaidYesterdayBy ? (
                        <div className="text-xs font-semibold text-rose-600">
                          Marked paid by {c.markedPaidYesterdayBy} — not active yet
                        </div>
                      ) : (
                        <div className="text-xs text-gray-500">
                          {c.lastCallOutcome
                            ? `Call: ${c.lastCallOutcome}${
                                c.lastCallNote ? ` — ${c.lastCallNote}` : ""
                              }`
                            : "Followed up"}
                        </div>
                      )}
                    </div>
                    <Badge
                      tone={
                        c.status === "Suspended"
                          ? "bg-rose-50 text-rose-700 border-rose-200"
                          : "bg-amber-50 text-amber-700 border-amber-200"
                      }
                    >
                      {c.status}
                    </Badge>
                  </button>
                ))}
              </div>
            )}
          </Modal.Body>
        </Modal>
      )}

      {/* Grand total — already inactive + about to go inactive in the next 7 days */}
      <section className={`${panelClass} mb-6`}>
        <div className="border-b border-gray-100 px-5 py-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
            All inactive / about to go inactive
          </h2>
        </div>
        <div className="flex flex-wrap gap-2 px-5 py-3">
          <div className="min-w-[160px] rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-center">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
              Already inactive — to follow up
            </div>
            <div className="mt-0.5 text-lg font-semibold tabular-nums text-rose-600">
              {overdue?.total ?? 0}
            </div>
          </div>
          <div className="min-w-[160px] rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-center">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
              In modem recovery
            </div>
            <div className="mt-0.5 text-lg font-semibold tabular-nums text-indigo-600">
              {overdue?.inModemRecovery ?? 0}
            </div>
          </div>
          <div className="min-w-[160px] rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-center">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
              Upcoming inactive (7d)
            </div>
            <div className="mt-0.5 text-lg font-semibold tabular-nums text-amber-600">
              {forecast?.total ?? 0}
            </div>
          </div>
          <div className="min-w-[160px] rounded-xl border border-cyan-200 bg-cyan-50 px-3 py-2 text-center">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-cyan-700">
              Combined total
            </div>
            <div className="mt-0.5 text-lg font-semibold tabular-nums text-cyan-700">
              {(overdue?.allInactive ?? overdue?.total ?? 0) + (forecast?.total ?? 0)}
            </div>
          </div>
        </div>
      </section>

      {/* Already overdue — currently Inactive / Suspended, bucketed by days down.
          Each bucket is clickable — it expands into the list of customers that
          make up its count, the same pattern as the Upcoming panel below. */}
      <section className={`${panelClass} mb-6`}>
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
            Already inactive — by days overdue
          </h2>
          <span className="text-xs text-gray-400">
            {overdue?.total ?? 0} to follow up
            {overdue?.inModemRecovery
              ? ` · ${overdue.inModemRecovery} already in modem recovery`
              : ""}
          </span>
        </div>
        <div className="flex flex-wrap gap-2 px-5 py-3">
          {[
            ["0-10", "text-amber-600"],
            ["11-20", "text-amber-600"],
            ["21-30", "text-orange-600"],
            ["31-40", "text-rose-600"],
            ["41-50", "text-rose-600"],
            ["50+", "text-rose-700"],
          ].map(([key, tone]) => {
            const bucket = overdue?.buckets?.[key];
            const count = (typeof bucket === "object" ? bucket?.count : bucket) ?? 0;
            const isOpen = expandedOverdueBucket === key;
            return (
              <button
                key={key}
                type="button"
                disabled={!count}
                onClick={() =>
                  setExpandedOverdueBucket((cur) => (cur === key ? null : key))
                }
                className={`min-w-[92px] rounded-xl border px-3 py-2 text-center transition ${
                  isOpen
                    ? "border-cyan-400 bg-cyan-50"
                    : "border-gray-200 bg-gray-50 hover:border-cyan-300"
                } ${count ? "cursor-pointer" : "cursor-default opacity-60"}`}
              >
                <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                  {key} days
                </div>
                <div
                  className={`mt-0.5 text-lg font-semibold tabular-nums ${tone}`}
                >
                  {count}
                </div>
              </button>
            );
          })}
        </div>
        {expandedOverdueBucket && (
          <div className="max-h-64 overflow-y-auto border-t border-gray-100">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-2 text-left">Customer</th>
                  <th className="px-4 py-2 text-left">Customer ID</th>
                  <th className="px-4 py-2 text-left">Phone</th>
                  <th className="px-4 py-2 text-center">Expiry</th>
                  <th className="px-4 py-2 text-center">Days overdue</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(overdue?.buckets?.[expandedOverdueBucket]?.customers || []).map(
                  (c) => (
                    <tr key={c.customerKey} className="hover:bg-amber-50/40">
                      <td className="px-4 py-2 font-medium text-gray-800">
                        {c.name || c.userId}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 tabular-nums text-gray-500">
                        {c.userId || "—"}
                      </td>
                      <td className="px-4 py-2 text-gray-500">
                        {c.phone || "—"}
                      </td>
                      <td className="px-4 py-2 text-center text-gray-500">
                        {fmtDate(c.expiryDate)}
                      </td>
                      <td className="px-4 py-2 text-center tabular-nums text-rose-600">
                        {c.daysOverdue ?? "—"}
                      </td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Already transferred to modem recovery (or closed out there) — kept
            out of the day cards above and shown by status instead. */}
        {(overdue?.modemGroups || []).length > 0 && (
          <div className="border-t border-gray-100">
            <div className="flex items-center justify-between px-5 pt-3">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Already transferred — by status
              </h3>
              <span className="text-xs text-gray-400">
                {overdue?.inModemRecovery ?? 0} customers
              </span>
            </div>
            <div className="flex flex-wrap gap-2 px-5 py-3">
              {(overdue?.modemGroups || []).map((g) => {
                const isOpen = expandedModemGroup === g.key;
                return (
                  <button
                    key={g.key}
                    type="button"
                    disabled={!g.count}
                    onClick={() =>
                      setExpandedModemGroup((cur) => (cur === g.key ? null : g.key))
                    }
                    className={`min-w-[120px] rounded-xl border px-3 py-2 text-center transition ${
                      isOpen
                        ? "border-indigo-400 bg-indigo-50"
                        : "border-gray-200 bg-gray-50 hover:border-indigo-300"
                    } ${g.count ? "cursor-pointer" : "cursor-default opacity-60"}`}
                  >
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                      {g.label}
                    </div>
                    <div className="mt-0.5 text-lg font-semibold tabular-nums text-indigo-600">
                      {g.count}
                    </div>
                  </button>
                );
              })}
            </div>
            {expandedModemGroup && (
              <div className="max-h-64 overflow-y-auto border-t border-gray-100">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
                      <th className="px-4 py-2 text-left">Customer</th>
                      <th className="px-4 py-2 text-left">Customer ID</th>
                      <th className="px-4 py-2 text-left">Phone</th>
                      <th className="px-4 py-2 text-left">With</th>
                      <th className="px-4 py-2 text-center">Expiry</th>
                      <th className="px-4 py-2 text-center">Days overdue</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {(
                      (overdue?.modemGroups || []).find((g) => g.key === expandedModemGroup)
                        ?.customers || []
                    ).map((c) => (
                      <tr key={c.customerKey} className="hover:bg-indigo-50/40">
                        <td className="px-4 py-2 font-medium text-gray-800">
                          {c.name || c.userId}
                        </td>
                        <td className="whitespace-nowrap px-4 py-2 tabular-nums text-gray-500">
                          {c.userId || "—"}
                        </td>
                        <td className="px-4 py-2 text-gray-500">{c.phone || "—"}</td>
                        <td className="px-4 py-2 text-gray-500">
                          {c.modemCase?.assignedToName || "—"}
                        </td>
                        <td className="px-4 py-2 text-center text-gray-500">
                          {fmtDate(c.expiryDate)}
                        </td>
                        <td className="px-4 py-2 text-center tabular-nums text-rose-600">
                          {c.daysOverdue ?? "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </section>

      {/* Upcoming — active subscribers due to go inactive over the next 7 days */}
      <section className={`${panelClass} mb-6`}>
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
            Upcoming inactive — next 7 days
          </h2>
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-400">
              {forecast?.total ?? 0} subscribers
            </span>
            <button
              className={btnGhost}
              onClick={() =>
                downloadWorkbook(partnerCode, {
                  view: "customers",
                  bucket: "expiring7",
                })
              }
            >
              Download
            </button>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 px-5 py-3">
          {(forecast?.byDay || []).map((d, i) => {
            const isOpen = expandedUpcomingDay === d.date;
            return (
              <button
                key={d.date}
                type="button"
                disabled={!d.count}
                onClick={() =>
                  setExpandedUpcomingDay((cur) => (cur === d.date ? null : d.date))
                }
                className={`rounded-xl border px-3 py-2 text-center transition ${
                  isOpen
                    ? "border-cyan-400 bg-cyan-50"
                    : "border-gray-200 bg-gray-50 hover:border-cyan-300"
                } ${d.count ? "cursor-pointer" : "cursor-default opacity-60"}`}
              >
                <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                  Day {i + 1} · {fmtDate(d.date)}
                </div>
                <div className="mt-0.5 text-lg font-semibold tabular-nums text-amber-600">
                  {d.count}
                </div>
              </button>
            );
          })}
          {!(forecast?.byDay || []).length && (
            <span className="text-xs text-gray-400">
              No active subscribers expiring in the next 7 days.
            </span>
          )}
        </div>
        {expandedUpcomingDay && (
          <div className="max-h-64 overflow-y-auto border-t border-gray-100">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-2 text-left">Customer</th>
                  <th className="px-4 py-2 text-left">Customer ID</th>
                  <th className="px-4 py-2 text-left">Phone</th>
                  <th className="px-4 py-2 text-center">Expiry</th>
                  <th className="px-4 py-2 text-center">In (days)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(
                  (forecast?.byDay || []).find((d) => d.date === expandedUpcomingDay)
                    ?.customers || []
                ).map((c) => (
                  <tr key={c.customerKey} className="hover:bg-amber-50/40">
                    <td className="px-4 py-2 font-medium text-gray-800">
                      {c.name || c.userId}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 tabular-nums text-gray-500">
                      {c.userId || "—"}
                    </td>
                    <td className="px-4 py-2 text-gray-500">
                      {c.phone || "—"}
                    </td>
                    <td className="px-4 py-2 text-center text-gray-500">
                      {fmtDate(c.expiryDate)}
                    </td>
                    <td className="px-4 py-2 text-center tabular-nums text-amber-600">
                      {c.expiringInDays}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className={panelClass}>
        <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 px-5 py-3">
          <label
            className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border border-gray-300 bg-white px-2 py-1"
            style={{ backgroundColor: "#fff" }}
            title="Partner code"
          >
            <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
              Partner
            </span>
            <select
              className="bg-white text-xs font-semibold text-gray-800 outline-none"
              style={{ backgroundColor: "#fff", color: "#1f2937" }}
              value={viewAll ? "ALL" : partnerCode}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "ALL") {
                  setViewAll(true);
                } else {
                  setViewAll(false);
                  setPartnerCode(v);
                }
                setPage(1);
              }}
            >
              <option
                value="ALL"
                style={{ color: "#1f2937", backgroundColor: "#fff" }}
              >
                All partners
              </option>
              {(allPartnerCodes.length ? allPartnerCodes : [partnerCode]).map(
                (c) => (
                  <option
                    key={c}
                    value={c}
                    style={{ color: "#1f2937", backgroundColor: "#fff" }}
                  >
                    {c}
                  </option>
                ),
              )}
            </select>
          </label>
          <select
            className={WHITE_FIELD}
            style={WHITE_FIELD_STYLE}
            value={state}
            onChange={(e) => changeFilter(setState)(e.target.value)}
          >
            {FOLLOWUP_STATES.map((s) => (
              <option key={s.key} value={s.key} style={WHITE_OPTION_STYLE}>
                {s.label}
              </option>
            ))}
          </select>
          <select
            className={WHITE_FIELD}
            style={WHITE_FIELD_STYLE}
            value={bucket}
            onChange={(e) => changeFilter(setBucket)(e.target.value)}
          >
            <option value="" style={WHITE_OPTION_STYLE}>
              All buckets
            </option>
            <option value="Inactive" style={WHITE_OPTION_STYLE}>
              Inactive
            </option>
            <option value="Suspended" style={WHITE_OPTION_STYLE}>
              Suspended
            </option>
            <option value="lapsed" style={WHITE_OPTION_STYLE}>
              Inactive + Suspended
            </option>
            <option value="active" style={WHITE_OPTION_STYLE}>
              Active
            </option>
            <option value="expiring7" style={WHITE_OPTION_STYLE}>
              Expiring 0-7 days
            </option>
          </select>
          <select
            className={WHITE_FIELD}
            style={WHITE_FIELD_STYLE}
            value={sort}
            onChange={(e) => changeFilter(setSort)(e.target.value)}
          >
            <option value="daysDown" style={WHITE_OPTION_STYLE}>
              Longest lapsed first
            </option>
            <option value="recent" style={WHITE_OPTION_STYLE}>
              Newest first
            </option>
            <option value="oldest" style={WHITE_OPTION_STYLE}>
              Oldest first
            </option>
            <option value="priority" style={WHITE_OPTION_STYLE}>
              Priority
            </option>
          </select>
          <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
            {dateFilterField}
          </span>
          <input
            type="date"
            className={WHITE_FIELD}
            style={WHITE_FIELD_STYLE}
            value={fromDate}
            max={toDate || undefined}
            onChange={(e) => changeFilter(setFromDate)(e.target.value)}
            title={`From (${dateFilterField})`}
          />
          <span className="text-xs text-gray-400">to</span>
          <input
            type="date"
            className={WHITE_FIELD}
            style={WHITE_FIELD_STYLE}
            value={toDate}
            min={fromDate || undefined}
            onChange={(e) => changeFilter(setToDate)(e.target.value)}
            title={`To (${dateFilterField})`}
          />
          {(fromDate || toDate) && (
            <button
              type="button"
              className={btnGhost}
              onClick={() => {
                setFromDate("");
                setToDate("");
                setPage(1);
              }}
            >
              Clear dates
            </button>
          )}
          <form
            className="ml-auto flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setQLive(q.trim());
              setPage(1);
            }}
          >
            <input
              className={`${fieldClass} w-52`}
              style={WHITE_FIELD_STYLE}
              placeholder="Name / phone / id"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <button className={btnGhost} type="submit">
              Search
            </button>
            <button
              type="button"
              className={btnGhost}
              title="Excel: Overdue + Expiring 0-6 days + Open follow-ups"
              onClick={() =>
                downloadWorkbook(partnerCode, { view: "followups" })
              }
            >
              Download sheet
            </button>
          </form>
        </div>

        {!isAllView && selected.size > 0 && (
          <div className="flex items-center gap-3 border-b border-cyan-100 bg-cyan-50/60 px-5 py-2 text-sm">
            <span className="font-semibold text-cyan-800">
              {selected.size} selected
            </span>
            <button
              className="rounded-lg bg-cyan-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-cyan-500"
              onClick={bulkTransfer}
            >
              Transfer {selected.size} → Modem Recovery
            </button>
            <button
              className="text-xs text-gray-500 hover:text-gray-700"
              onClick={() => setSelected(new Set())}
            >
              Clear
            </button>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
                {isAllView ? (
                  <th className="px-4 py-3 text-left">Partner</th>
                ) : (
                  <th className="px-3 py-3 text-center">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      aria-label="Select all"
                    />
                  </th>
                )}
                <th className="px-4 py-3 text-left">Customer</th>
                <th className="px-4 py-3 text-center">Bucket</th>
                <th className="px-4 py-3 text-center">Down since</th>
                <th className="px-4 py-3 text-center">Days down</th>
                <th className="px-4 py-3 text-center">State</th>
                <th className="px-4 py-3 text-center">Priority</th>
                <th className="px-4 py-3 text-left">Assigned</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isFetching ? (
                <tr>
                  <td
                    colSpan={9}
                    className="px-4 py-12 text-center text-gray-400"
                  >
                    Loading…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={9}
                    className="px-4 py-12 text-center text-gray-400"
                  >
                    No follow-ups.
                  </td>
                </tr>
              ) : (
                rows.map((f) => {
                  const parked = f.state === "parked";
                  // The parked line reflects the linked case's LIVE outcome,
                  // not a static "transferred" label — once it's recovered or
                  // handed over, this row should say so.
                  const kase = f.modemCaseId;
                  const modemLabel = !kase
                    ? "Transferred to modem recovery"
                    : kase.handedOver
                      ? "Handed over to the company"
                      : kase.state === "recharged"
                        ? "Recharged"
                        : kase.state === "recovered"
                          ? "Modem recovered"
                          : kase.state === "untraceable"
                            ? "Untraceable"
                            : "Transferred to modem recovery";
                  // Who currently owns the case — can differ from this
                  // follow-up's own assignedTo once the case has been
                  // reassigned (e.g. an "untraceable" hand-off to someone
                  // else). Falls back to the follow-up's assignee when the
                  // case itself has none on file.
                  const modemAssigneeName =
                    kase?.assignedTo?.name || f.assignedTo?.name || "";
                  const modemLabelTone =
                    kase &&
                    (kase.handedOver ||
                      ["recovered", "recharged"].includes(kase.state))
                      ? "text-emerald-600"
                      : "text-rose-600";
                  return (
                    <tr
                      key={f._id}
                      className={
                        parked
                          ? "bg-gray-50 text-gray-400"
                          : "hover:bg-cyan-50/30"
                      }
                    >
                      {isAllView ? (
                        <td className="px-4 py-2.5 text-xs font-semibold text-gray-500">
                          {f.partnerCode}
                        </td>
                      ) : (
                        <td className="px-3 py-2.5 text-center">
                          {f.isOpen ? (
                            <input
                              type="checkbox"
                              checked={selected.has(f.customerKey)}
                              onChange={() => toggleSel(f.customerKey)}
                              aria-label={`Select ${f.name || f.userId}`}
                            />
                          ) : null}
                        </td>
                      )}
                      <td className="px-4 py-2.5">
                        <button
                          type="button"
                          className="font-medium text-gray-800 underline decoration-dotted hover:text-cyan-700"
                          onClick={() =>
                            setDetailFor({
                              partnerCode: f.partnerCode || partnerCode,
                              customerKey: f.customerKey,
                            })
                          }
                        >
                          {f.name || f.userId}
                        </button>
                        {f.verificationStatus === "failed" && (
                          <div className="mt-0.5">
                            <Badge tone="bg-rose-50 text-rose-700 border-rose-200">
                              Still inactive — marked paid, not confirmed
                            </Badge>
                          </div>
                        )}
                        {parked && (
                          <div
                            className={`mt-0.5 text-xs font-medium ${modemLabelTone}`}
                          >
                            → {modemLabel}
                            {f.movedToModemBy?.name &&
                              ` by ${firstName(f.movedToModemBy.name)}`}
                            {modemAssigneeName &&
                              ` to ${firstName(modemAssigneeName)}`}
                          </div>
                        )}
                        <div
                          className="flex flex-wrap gap-x-1 font-bold"
                          style={{ color: "#222222", fontSize: 14 }}
                        >
                          <span className="whitespace-nowrap">
                            m:{f.phone || "—"},
                          </span>
                          <span className="whitespace-nowrap">
                            id:{f.userId}
                          </span>
                        </div>
                        {(() => {
                          // Parked rows already show the transfer line above —
                          // don't repeat it as the activity chip.
                          if (parked) return null;
                          // Remarks on Active rows (saved via Call/Paid) are
                          // backed by real callLog entries from the server now
                          // — shown exactly the same way as a real follow-up's
                          // history below. Only fall back to the static
                          // "Expiring in Xd" line when there's genuinely
                          // nothing logged yet.
                          const hist = callHistoryOf(f);
                          const last =
                            hist[0] ||
                            (f.isSynthetic && localRemarks[f.customerKey]
                              ? {
                                  at: new Date().toISOString(),
                                  note: localRemarks[f.customerKey],
                                  by: null,
                                }
                              : null);
                          const tone = last ? followupTone(last) : "#1d4ed8";
                          if (!last) {
                            if (f.isSynthetic) {
                              return (
                                <div className="mt-1 text-xs text-cyan-700">
                                  {f.expiringInDays != null
                                    ? `Expiring in ${f.expiringInDays}d`
                                    : "Still active"}
                                </div>
                              );
                            }
                            return (
                              <div className="mt-1 text-xs text-gray-400">
                                No follow-up yet
                              </div>
                            );
                          }
                          return (
                            <button
                              type="button"
                              onClick={() => setHistoryFollowup(f)}
                              title="View all follow-ups for this customer"
                              className="mt-1 flex max-w-[300px] items-center gap-1.5 text-[13px] transition hover:opacity-75"
                              style={{ color: tone }}
                            >
                              <span className="rounded-full bg-slate-200 px-1.5 py-0.5 text-[10px] font-bold text-slate-700">
                                {authorChip(last.by, meId)}
                              </span>
                              <span
                                className="font-semibold"
                                style={{ color: tone }}
                              >
                                {fmtDate(last.at)}
                              </span>
                              <span
                                className="truncate font-medium"
                                style={{ color: tone }}
                              >
                                — {last.note || last.outcome || "call logged"}
                              </span>
                              {hist.length > 1 && (
                                <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">
                                  {hist.length}
                                </span>
                              )}
                            </button>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-2.5 text-center">
                        {f.lapseBucket}
                      </td>
                      <td className="px-4 py-2.5 text-center text-gray-500">
                        {fmtDate(f.deactivatedOn)}
                      </td>
                      <td className="px-4 py-2.5 text-center">
                        <span
                          className={`inline-block min-w-[2.5rem] rounded-md px-1.5 py-0.5 text-xs font-semibold tabular-nums ${
                            f.daysDown >= 30
                              ? "bg-rose-100 text-rose-700"
                              : f.daysDown >= 8
                                ? "bg-amber-100 text-amber-700"
                                : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {f.daysDown ?? "—"}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-center">
                        <Badge
                          tone={
                            f.isOpen
                              ? "bg-amber-50 text-amber-700 border-amber-200"
                              : "bg-gray-100 text-gray-500 border-gray-200"
                          }
                        >
                          {f.state}
                        </Badge>
                      </td>
                      <td className="px-4 py-2.5 text-center">
                        <Badge
                          tone={
                            f.priority === "high"
                              ? "bg-rose-50 text-rose-700 border-rose-200"
                              : f.priority === "medium"
                                ? "bg-amber-50 text-amber-700 border-amber-200"
                                : "bg-gray-100 text-gray-500 border-gray-200"
                          }
                        >
                          {f.priority}
                        </Badge>
                      </td>
                      <td className="px-4 py-2.5 text-xs text-gray-600">
                        {f.assignedTo?.name || "—"}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        {(f.isOpen || f.isSynthetic) && (
                          <div className="flex flex-col items-end gap-1.5">
                            <div className="inline-flex flex-wrap justify-end gap-1">
                              <button
                                className={`rounded-lg border px-4 py-2 text-xs font-semibold transition ${
                                  getCallDraft(f._id).open
                                    ? "border-cyan-500 bg-cyan-50 text-cyan-700"
                                    : "border-gray-200 text-gray-700 hover:border-cyan-400 hover:text-cyan-700"
                                }`}
                                onClick={() => toggleCall(f._id)}
                              >
                                Call
                              </button>
                              <button
                                className={btnGhost}
                                onClick={() => markPaid(f)}
                              >
                                Paid
                              </button>
                              <button
                                className={btnGhost}
                                onClick={() => transfer(f)}
                                style={{
                                  alignItems: "center",
                                  display: "flex",
                                }}
                              >
                                <span> →</span>
                                <span> Modem</span>
                              </button>
                            </div>
                            {getCallDraft(f._id).open && (
                              <div className="flex items-center gap-1.5">
                                <select
                                  className={WHITE_FIELD}
                                  style={WHITE_FIELD_STYLE}
                                  value={getCallDraft(f._id).outcome}
                                  onChange={(e) =>
                                    setCallDraft(f._id, {
                                      outcome: e.target.value,
                                    })
                                  }
                                >
                                  {CALL_OUTCOMES.map((o) => (
                                    <option
                                      key={o}
                                      value={o}
                                      style={WHITE_OPTION_STYLE}
                                    >
                                      {o}
                                    </option>
                                  ))}
                                </select>
                                <input
                                  autoFocus
                                  className={`${WHITE_FIELD} w-48`}
                                  style={WHITE_FIELD_STYLE}
                                  placeholder="Add a remark…"
                                  value={getCallDraft(f._id).note}
                                  onChange={(e) =>
                                    setCallDraft(f._id, {
                                      note: e.target.value,
                                    })
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") saveCall(f);
                                    if (e.key === "Escape") toggleCall(f._id);
                                  }}
                                />
                                <button
                                  className="rounded-lg bg-cyan-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-cyan-500 disabled:opacity-50"
                                  disabled={savingCall === f._id}
                                  onClick={() => saveCall(f)}
                                >
                                  {savingCall === f._id ? "…" : "Save"}
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-5 py-3 text-xs text-gray-500">
          <span>{data?.totalCount ?? 0} total</span>
          <div className="flex items-center gap-2">
            <button
              className={btnGhost}
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Prev
            </button>
            <span>
              {page} / {totalPages}
            </span>
            <button
              className={btnGhost}
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </section>

      {historyFollowup && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-900/40 p-4"
          onClick={() => setHistoryFollowup(null)}
        >
          <div
            className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {historyFollowup.name || historyFollowup.userId}
                </h3>
                <p
                  className="mt-0.5 flex flex-wrap gap-x-1 font-bold"
                  style={{ color: "#222222", fontSize: 14 }}
                >
                  <span className="whitespace-nowrap">
                    m:{historyFollowup.phone || "—"},
                  </span>
                  <span className="whitespace-nowrap">
                    id:{historyFollowup.userId}
                  </span>
                </p>
              </div>
              <button
                type="button"
                className="rounded-full border-2 border-gray-300 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-gray-600"
                onClick={() => setHistoryFollowup(null)}
              >
                Close
              </button>
            </div>
            <div className="mt-4 max-h-96 overflow-y-auto rounded-lg border border-gray-200">
              {callHistoryOf(historyFollowup).map((h, i) => (
                <div
                  key={i}
                  className={`px-4 py-2 text-sm ${i % 2 === 1 ? "bg-gray-50" : "bg-white"}`}
                  style={{ color: followupTone(h) }}
                >
                  <span className="font-semibold">{fmtDate(h.at)}</span>{" "}
                  <span
                    className="rounded-full bg-slate-200 px-1.5 py-0.5 text-[10px] font-bold text-slate-700"
                    title={h.by?.name || ""}
                  >
                    {authorChip(h.by, meId)}
                  </span>{" "}
                  {h.outcome && (
                    <span className="text-xs opacity-70">[{h.outcome}]</span>
                  )}{" "}
                  <span className="font-medium">
                    — {h.note || "(no remark)"}
                  </span>
                </div>
              ))}
              {callHistoryOf(historyFollowup).length === 0 && (
                <div className="px-4 py-6 text-center text-xs text-gray-400">
                  No follow-ups logged yet.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {detailFor && (
        <CustomerDrawer
          partnerCode={detailFor.partnerCode}
          customerKey={detailFor.customerKey}
          onClose={() => setDetailFor(null)}
        />
      )}
    </>
  );
};

const Stat = ({ label, value, tone, onClick }) => {
  const body = (
    <>
      <div className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
        {label}
      </div>
      <div
        className={`mt-1 text-2xl font-semibold tabular-nums ${tone || "text-gray-900"}`}
      >
        {value ?? "—"}
      </div>
    </>
  );
  if (!onClick) return <div className={`${panelClass} px-4 py-3`}>{body}</div>;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${panelClass} px-4 py-3 text-left transition hover:ring-2 hover:ring-cyan-400/40`}
    >
      {body}
    </button>
  );
};

export default NcsFollowups;
